/**
 * Forte Plus — visszajelzés a kártyáról indított kosárba tevésre.
 *
 * A termék képe elrepül a kosár ikonjához, a kosár megrezzen, és néhány szív száll fel a gomb
 * körül. A szívek CSAK akkor indulnak, ha a kosár darabszáma tényleg nőtt (/cart.js) — így az
 * ünneplés sosem hazudik egy sikertelen hozzáadásnál.
 *
 * Minden elem a <body> végére kerül, fixen pozicionálva, és az animáció végén törlődik; a
 * kártya elrendezéséhez nem nyúl. Csökkentett mozgás kérése esetén semmi nem fut.
 */
(() => {
  const FLY_MS = 620;
  const HEART_MS = 1100;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  const cartIcon = () =>
    document.querySelector('.header-actions__action[aria-label*="Krep" i], cart-icon, [class*="cart-icon"]');

  async function cart() {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      return await response.json();
    } catch {
      return null;
    }
  }

  async function cartCount() {
    return (await cart())?.item_count ?? null;
  }

  const cardButton = (card) => card.querySelector('.quick-add__button--add, .quick-add__button--choose');

  const cardHandle = (card) =>
    card
      .querySelector('a[href*="/products/"]')
      ?.getAttribute('href')
      .split('/products/')[1]
      ?.split(/[?#]/)[0];

  /**
   * A pipa addig marad a gombon, amíg a termék a kosárban van — beleértve az oldal újratöltését
   * és a kosárból való törlést is, ezért a kosár tartalmából számoljuk, nem a kattintásból.
   */
  async function syncWithCart() {
    const data = await cart();
    if (!data) return;
    const inCart = new Set(data.items.map((item) => item.handle));
    for (const card of document.querySelectorAll('product-card')) {
      const button = cardButton(card);
      if (!button) continue;
      button.classList.toggle('ep-added', inCart.has(cardHandle(card)));
    }
  }

  /** A kártya képe elrepül a kosárig. */
  function flyToCart(card) {
    const image = card.querySelector('img');
    const target = cartIcon();
    if (!image || !target) return;

    const from = image.getBoundingClientRect();
    const to = target.getBoundingClientRect();

    const ghost = document.createElement('img');
    ghost.src = image.currentSrc || image.src;
    ghost.alt = '';
    ghost.className = 'ep-fly-ghost';
    ghost.style.cssText = `left:${from.left}px;top:${from.top}px;width:${from.width}px;height:${from.height}px`;
    document.body.append(ghost);

    const dx = to.left + to.width / 2 - (from.left + from.width / 2);
    const dy = to.top + to.height / 2 - (from.top + from.height / 2);

    ghost
      .animate(
        [
          { transform: 'translate(0,0) scale(1)', opacity: 0.95, borderRadius: '14px' },
          { transform: `translate(${dx * 0.55}px, ${dy * 0.35 - 60}px) scale(.55)`, opacity: 0.9, offset: 0.55 },
          { transform: `translate(${dx}px, ${dy}px) scale(.08)`, opacity: 0, borderRadius: '50%' },
        ],
        { duration: FLY_MS, easing: 'cubic-bezier(.45,.05,.3,1)' }
      )
      .finished.catch(() => {})
      .finally(() => ghost.remove());
  }

  function pulseCart() {
    const target = cartIcon();
    if (!target) return;
    target
      .animate(
        [
          { transform: 'scale(1)' },
          { transform: 'scale(1.28)' },
          { transform: 'scale(.94)' },
          { transform: 'scale(1)' },
        ],
        { duration: 520, easing: 'ease-out' }
      )
      .finished.catch(() => {});
  }

  /** Szívek a gomb fölött. */
  function hearts(button) {
    const box = button.getBoundingClientRect();
    const layer = document.createElement('div');
    layer.className = 'ep-heart-layer';
    document.body.append(layer);

    for (let i = 0; i < 7; i += 1) {
      const heart = document.createElement('span');
      heart.className = 'ep-heart';
      heart.textContent = i % 3 === 0 ? '✨' : '💜';
      heart.style.cssText = `left:${box.left + box.width / 2}px;top:${box.top}px`;
      layer.append(heart);

      const drift = (Math.random() - 0.5) * 160;
      heart.animate(
        [
          { transform: 'translate(-50%, 0) scale(.5)', opacity: 0 },
          { transform: `translate(calc(-50% + ${drift * 0.4}px), -42px) scale(1.1)`, opacity: 1, offset: 0.3 },
          { transform: `translate(calc(-50% + ${drift}px), -150px) scale(.7)`, opacity: 0 },
        ],
        { duration: HEART_MS, delay: i * 55, easing: 'cubic-bezier(.3,.7,.4,1)', fill: 'forwards' }
      );
    }

    setTimeout(() => layer.remove(), HEART_MS + 7 * 55 + 120);
  }

  document.addEventListener(
    'click',
    async (event) => {
      const button = event.target.closest?.('.quick-add__button--add, .quick-add__button--choose');
      if (!button || reduced.matches) return;

      const card = button.closest('product-card');
      if (!card) return;

      const before = await cartCount();
      flyToCart(card);

      // megvárjuk, amíg a téma elvégzi a hozzáadást, és csak valódi növekedésnél ünneplünk
      for (let attempt = 0; attempt < 12; attempt += 1) {
        await new Promise((r) => setTimeout(r, 250));
        const after = await cartCount();
        if (before == null || after == null) return;
        if (after > before) {
          pulseCart();
          hearts(button);
          button.classList.add('ep-added'); // marad, amíg a termék a kosárban van
          return;
        }
      }
    },
    true
  );

  // induláskor, visszatéréskor és a kosár számlálójának minden változásakor újraszinkronizálunk,
  // így a pipa akkor is eltűnik, ha a vevő a fiókból kiveszi a terméket
  const start = () => {
    syncWithCart();
    const counter = document.querySelector('cart-icon, [class*="cart-icon"], .header-actions__action[aria-label*="Krep" i]');
    if (counter) new MutationObserver(() => syncWithCart()).observe(counter, { childList: true, subtree: true, characterData: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  window.addEventListener('pageshow', syncWithCart);
})();
